import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { finalize, first, takeUntil } from 'rxjs/operators';
import { PopoverService } from '../../../../../storybook/services/popover/popover.service';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ApiService } from '../../../../../core/services/api/api.service';
import { FilterConditionType, IFilterCondition } from '@pdqdev/pdq-shared/dist/interfaces';
import { DropdownOption } from '../../../../../storybook/components/dropdown/dropdown.component';
import { Moment } from 'moment';
import { ToastService } from '../../../../../storybook/services/toast/toast.service';

enum SyncToastMessage {
  SYNC_SUCCESS = 'Sync process started',
  SYNC_ERROR = 'Failed to start sync process',
  SERVER_ERROR = 'Server error',
}
@Component({
  selector: 'sync-orders',
  templateUrl: './sync-orders.component.html',
  styleUrls: ['./sync-orders.component.scss'],
  standalone: false,
})
export class SyncOrdersComponent implements OnInit, OnDestroy {
  destroy$ = new Subject();
  isOpen = false;
  allConditions: IFilterCondition[] = [];
  defaultCondition: IFilterCondition;
  conditionsForm: UntypedFormArray;
  NAME_TO_CONDITION: { [key: string]: IFilterCondition } = {};
  syncLoading: boolean = false;
  ConditionType = FilterConditionType;
  constructor(
    private toastService: ToastService,
    private popoverService: PopoverService,
    private apiService: ApiService,
  ) {}
  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnInit(): void {
    this.initSubscriptions();
    this.getConditionOptions()
      .pipe(first())
      .subscribe(
        (res) => {
          this.allConditions = res;
          this.defaultCondition = res.find((condition) => condition.isDefault);
          this.initForm();
          this.initConditionMap();
        },
        (err) => {
          console.log(err);
          this.toastService.showToast({ mode: 'danger', message: SyncToastMessage.SERVER_ERROR });
        },
      );
  }
  get availableConditions() {
    return this.allConditions.filter(
      (condition) => !this.conditionsForm.controls.some((control) => control.value.conditionName === condition.name),
    );
  }
  closePopover() {
    this.popoverService.setState(true);
    this.initForm();
  }
  setCondition(conditionOption: DropdownOption, index: number) {
    (this.conditionsForm.at(index) as UntypedFormGroup).controls.conditionName.setValue(conditionOption.value);
  }
  syncHandler() {
    this.syncLoading = true;
    const payload = this.prepareSyncPayload();
    this.apiService
      .post('sync/orders-by-condition', payload, { responseType: 'text' })
      .pipe(
        finalize(() => {
          this.syncLoading = false;
          this.closePopover();
        }),
      )
      .subscribe(
        () => {
          this.toastService.showToast({ mode: 'success', message: SyncToastMessage.SYNC_SUCCESS });
        },
        (err) => {
          console.log(err);
          this.toastService.showToast({ mode: 'danger', message: SyncToastMessage.SYNC_ERROR });
        },
      );
  }
  addCondition() {
    this.conditionsForm.push(this.createConditionControl(this.availableConditions.pop()));
  }
  private prepareSyncPayload() {
    const conditions = this.conditionsForm.controls
      .filter((control) => control.value?.conditionValue)
      .map((control) => ({
        ...this.NAME_TO_CONDITION[control.value.conditionName],
        value: control.value.conditionValue,
      }));

    return { conditions };
  }

  setDateRangeValue(value: { start: Moment; end: Moment }, index: number) {
    const { start, end } = value;
    if (!start || !end) {
      return;
    }
    const startDate = start.toISOString();
    const endDate = end.toISOString();
    this.conditionsForm
      .at(index)
      .setValue({ ...this.conditionsForm.at(index).value, conditionValue: { startDate, endDate } });
  }
  removeCondition(index: number) {
    this.conditionsForm.removeAt(index);
  }

  private createConditionControl(condition: IFilterCondition): UntypedFormGroup {
    return new UntypedFormGroup({
      conditionName: new UntypedFormControl(condition.name),
      conditionValue: new UntypedFormControl('', Validators.required),
    });
  }
  private initSubscriptions() {
    this.popoverService
      .getState()
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.isOpen = !state;
      });
  }
  private initForm() {
    this.conditionsForm = new UntypedFormArray([this.createConditionControl(this.defaultCondition)]);
  }
  private initConditionMap() {
    this.allConditions.forEach((condition) => {
      this.NAME_TO_CONDITION[condition.name] = condition;
    });
  }
  private getConditionOptions(): Observable<IFilterCondition[]> {
    return new Observable((subscriber) => {
      this.apiService.get('sync/filter-conditions').subscribe(
        (res: IFilterCondition[]) => {
          subscriber.next(res);
        },
        (err) => subscriber.error(err),
      );
    });
  }
}
